import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { when } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { PricingSimulator } from "@/pages/admin/PricingSimulator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LocationPicker } from "@/components/LocationPicker";

export const Num = ({ label, value, onChange, testid, step = "0.01", min = "0" }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <Input type="number" step={step} min={min} value={value} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid} className="h-9" />
  </div>
);

const Section = ({ title, sub, children }) => (
  <section className="space-y-4 rounded-xl border bg-card p-5">
    <div>
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
    {children}
  </section>
);

function AnchorEditor({ anchor, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const search = async () => {
    try {
      const { data } = await api.get("/geo/search", { params: { q } });
      setResults(data);
      if (!data.length) toast("No map results — enter coordinates manually");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const set = (k) => (e) => onChange({ ...anchor, [k]: k === "lat" || k === "lng" ? Number(e.target.value) : e.target.value });
  return (
    <Section title="GPS anchor" sub="Every fare is measured from this point: anchor → pickup → trip → back to anchor (only legs actually driven).">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Name</Label><Input value={anchor.name} onChange={set("name")} className="h-9" data-testid="anchor-name-input" /></div>
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Address</Label><Input value={anchor.address} onChange={set("address")} className="h-9" data-testid="anchor-address-input" /></div>
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Latitude</Label><Input type="number" step="any" value={anchor.lat} onChange={set("lat")} className="h-9" data-testid="anchor-lat-input" /></div>
        <div className="space-y-1"><Label className="text-xs text-muted-foreground">Longitude</Label><Input type="number" step="any" value={anchor.lng} onChange={set("lng")} className="h-9" data-testid="anchor-lng-input" /></div>
      </div>
      <div className="flex gap-2">
        <Input placeholder="Find exact pin by address" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="anchor-search-input" />
        <Button type="button" variant="outline" className="h-9" onClick={search} disabled={q.trim().length < 3} data-testid="anchor-search-button"><Search className="h-4 w-4" /></Button>
      </div>
      {results.map((r, i) => (
        <button key={i} type="button" onClick={() => { onChange({ ...anchor, lat: r.lat, lng: r.lng }); setResults([]); }} data-testid="anchor-search-result"
          className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-muted">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neon-cyan" /> <span>{r.address} <span className="font-mono text-muted-foreground">({r.lat.toFixed(5)}, {r.lng.toFixed(5)})</span></span>
        </button>
      ))}
    </Section>
  );
}

function VehiclesEditor({ vehicles, onChange }) {
  const set = (k, field, v) => onChange({ ...vehicles, [k]: { ...vehicles[k], [field]: v } });
  return (
    <Section title="Mileage rates" sub="Per mile, applied to each leg after rounding that leg down to whole miles.">
      {Object.entries(vehicles).map(([k, v]) => (
        <div key={k} className="space-y-3 rounded-lg border border-white/10 p-4" data-testid={`vehicle-rates-${k}`}>
          <div className="flex items-center justify-between">
            <p className="font-semibold">{v.label}</p>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Available to riders <Switch checked={v.available} onCheckedChange={(c) => set(k, "available", c)} data-testid={`vehicle-${k}-available`} />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Num label="Anchor → Pickup" value={v.to_pickup} onChange={(x) => set(k, "to_pickup", x)} testid={`rate-${k}-to-pickup`} />
            <Num label="Pickup → Destination" value={v.trip} onChange={(x) => set(k, "trip", x)} testid={`rate-${k}-trip`} />
            <Num label="Destination → Anchor" value={v.return} onChange={(x) => set(k, "return", x)} testid={`rate-${k}-return`} />
          </div>
        </div>
      ))}
    </Section>
  );
}

function TiersEditor({ rules, onChange }) {
  const tiers = rules.base_tiers;
  const setTier = (i, field, v) => onChange({ ...rules, base_tiers: tiers.map((t, j) => (j === i ? { ...t, [field]: v } : t)) });
  return (
    <Section title="Base tiers" sub="Chosen by the trip's farthest point from the anchor (road miles). Added on top of the rounded-down mileage.">
      <div className="space-y-2">
        {tiers.map((t, i) => (
          <div key={i} className="flex items-end gap-3" data-testid={`tier-row-${i}`}>
            <Num label="From (miles)" value={t.min_miles} step="1" onChange={(x) => setTier(i, "min_miles", x)} testid={`tier-${i}-min`} />
            <Num label="Base ($)" value={t.base} onChange={(x) => setTier(i, "base", x)} testid={`tier-${i}-base`} />
            <Button type="button" size="icon" variant="ghost" disabled={i === 0} onClick={() => onChange({ ...rules, base_tiers: tiers.filter((_, j) => j !== i) })} data-testid={`tier-${i}-remove`}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...rules, base_tiers: [...tiers, { min_miles: (tiers.at(-1)?.min_miles || 0) + 10, base: 0 }] })} data-testid="tier-add-button">
          <Plus className="mr-1 h-4 w-4" /> Add tier
        </Button>
        <Num label="Pricing review at (miles)" step="1" value={rules.review_miles} onChange={(x) => onChange({ ...rules, review_miles: x })} testid="review-miles-input" />
      </div>
    </Section>
  );
}

function WaitTiersEditor({ rules, onChange }) {
  const tiers = rules.wait_tiers;
  const setT = (i, k, v) => onChange({ ...rules, wait_tiers: tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  return (
    <Section title="Wait time" sub="Priced per stop (and at the destination of a round trip). Waits past the last tier need an admin quote.">
      {tiers.map((t, i) => (
        <div key={i} className="flex items-end gap-3" data-testid={`wait-tier-row-${i}`}>
          <Num label="Up to (minutes)" step="1" value={t.max_minutes} onChange={(x) => setT(i, "max_minutes", x)} testid={`wait-tier-${i}-max`} />
          <Num label="Fee ($)" value={t.fee} onChange={(x) => setT(i, "fee", x)} testid={`wait-tier-${i}-fee`} />
          <Button type="button" size="icon" variant="ghost" disabled={tiers.length === 1} onClick={() => onChange({ ...rules, wait_tiers: tiers.filter((_, j) => j !== i) })} data-testid={`wait-tier-${i}-remove`}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...rules, wait_tiers: [...tiers, { max_minutes: (tiers.at(-1)?.max_minutes || 0) + 15, fee: 0 }] })} data-testid="wait-tier-add-button">
        <Plus className="mr-1 h-4 w-4" /> Add wait tier
      </Button>
    </Section>
  );
}

function RecurringEditor({ rules, onChange }) {
  const r = rules.recurring;
  const set = (k) => (v) => onChange({ ...rules, recurring: { ...r, [k]: v } });
  return (
    <Section title="Recurring rides" sub="Weekly discount on the week's total once the week has enough ride legs. The discounted weekly total rounds down.">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Num label="Min legs / week" step="1" value={r.min_legs_per_week} onChange={set("min_legs_per_week")} testid="recurring-min-legs-input" />
        <Num label="End-of-week (%)" step="1" value={r.end_of_week_pct} onChange={set("end_of_week_pct")} testid="recurring-eow-pct-input" />
        <Num label="Prepaid (%)" step="1" value={r.prepaid_pct} onChange={set("prepaid_pct")} testid="recurring-prepaid-pct-input" />
        <Num label="Round down to ($)" step="1" value={r.round_down_to} onChange={set("round_down_to")} testid="recurring-round-input" />
      </div>
      <p className="text-xs text-muted-foreground">Booking fee is always <span className="font-semibold text-foreground">$0.00</span> and can't be changed.</p>
    </Section>
  );
}

function FuelReview({ rules, onChange, status }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const mark = async () => {
    try {
      await api.post("/admin/pricing/fuel-review", { note: note || null });
      toast.success("Fuel-price review recorded");
      setNote("");
      qc.invalidateQueries({ queryKey: ["adminPricing"] });
      qc.invalidateQueries({ queryKey: ["adminDashboard"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Section title="Fuel-price review" sub="A reminder to compare rates against fuel prices. Nothing changes automatically.">
      <div className={`rounded-lg border p-3 text-sm ${status.is_due ? "border-amber-400/50 bg-amber-400/10 text-amber-100" : "border-white/10"}`} data-testid="fuel-review-status">
        {status.is_due ? "Review due now" : `Next review ${when(status.due_at)}`} · last reviewed {status.last_reviewed_at ? `${when(status.last_reviewed_at)} by ${status.reviewed_by}` : "never"}
        {status.note && <p className="mt-1 text-xs text-muted-foreground">“{status.note}”</p>}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Num label="Every (days)" step="1" value={rules.fuel_review_days} onChange={(x) => onChange({ ...rules, fuel_review_days: x })} testid="fuel-review-days-input" />
        <Input placeholder="Note, e.g. gas $2.89/gal — no change" value={note} onChange={(e) => setNote(e.target.value)} className="h-9 flex-1" data-testid="fuel-review-note-input" />
        <Button type="button" variant="outline" className="h-9" onClick={mark} data-testid="fuel-review-mark-button">Mark reviewed</Button>
      </div>
    </Section>
  );
}

function SpecialLocations({ rules, onChange }) {
  const list = rules.location_adjustments || [];
  const setL = (next) => onChange({ ...rules, location_adjustments: next });
  const upd = (i, patch) => setL(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const roles = [["start", "Starting"], ["stop", "Stopping"], ["end", "Ending"]];
  return (
    <Section title="Special location pricing" sub="Up to 5 places. A flat amount is added (+) or taken off (-) when a ride starts, stops or ends there. If a ride matches more than one, only the biggest adjustment applies.">
      {list.map((l, i) => (
        <div key={i} className="space-y-2 rounded-lg border p-3" data-testid={`special-location-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="min-w-0 truncate text-sm font-semibold">{l.name || "Pick a place"}</p>
            <Button type="button" size="icon" variant="ghost" onClick={() => setL(list.filter((_, j) => j !== i))} data-testid={`special-location-${i}-remove`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          </div>
          <LocationPicker kind={`special-${i}`} label="Place" value={l.lat ? l : null} details={false} onChange={(p) => upd(i, p ? { name: p.name, lat: p.lat, lng: p.lng } : { lat: null, lng: null })} />
          <div className="grid grid-cols-2 gap-3">
            <Num label="Amount ($, use - for discount)" min="-500" value={l.amount} onChange={(x) => upd(i, { amount: x })} testid={`special-location-${i}-amount`} />
            <Num label="Radius (miles)" value={l.radius_miles} onChange={(x) => upd(i, { radius_miles: x })} testid={`special-location-${i}-radius`} />
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            {roles.map(([k, v]) => (
              <label key={k} className="flex items-center gap-1.5">
                <input type="checkbox" checked={l.applies_to.includes(k)} data-testid={`special-location-${i}-${k}`}
                  onChange={(e) => upd(i, { applies_to: e.target.checked ? [...l.applies_to, k] : l.applies_to.filter((x) => x !== k) })} />{v} here
              </label>
            ))}
          </div>
        </div>
      ))}
      {list.length < 5 && (
        <Button type="button" variant="ghost" size="sm" onClick={() => setL([...list, { name: "", lat: null, lng: null, radius_miles: 0.25, amount: 5, applies_to: ["start", "stop", "end"] }])} data-testid="special-location-add">
          <Plus className="mr-1 h-4 w-4" /> Add special location
        </Button>
      )}
    </Section>
  );
}

function ExtrasEditor({ rules, onChange }) {
  const a = rules.airport;
  const setA = (k, v) => onChange({ ...rules, airport: { ...a, [k]: v } });
  const setAp = (i, k, v) => setA("airports", a.airports.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <>
      <WaitTiersEditor rules={rules} onChange={onChange} />
      <SpecialLocations rules={rules} onChange={onChange} />
      <Section title="Airport component" sub="Added once when pickup, any stop or the destination is within the radius of an airport.">
        <div className="grid grid-cols-2 gap-3">
          <Num label="Amount ($)" value={a.amount} onChange={(x) => setA("amount", x)} testid="airport-amount-input" />
          <Num label="Radius (miles)" value={a.radius_miles} onChange={(x) => setA("radius_miles", x)} testid="airport-radius-input" />
        </div>
        {a.airports.map((ap, i) => (
          <div key={i} className="grid grid-cols-[70px_1fr_1fr] gap-2" data-testid={`airport-row-${i}`}>
            <Input value={ap.code} onChange={(e) => setAp(i, "code", e.target.value.toUpperCase())} className="h-9" />
            <Input type="number" step="any" value={ap.lat} onChange={(e) => setAp(i, "lat", Number(e.target.value))} className="h-9" />
            <Input type="number" step="any" value={ap.lng} onChange={(e) => setAp(i, "lng", Number(e.target.value))} className="h-9" />
          </div>
        ))}
      </Section>
      <RecurringEditor rules={rules} onChange={onChange} />
    </>
  );
}

function Versions() {
  const { data } = useQuery({ queryKey: ["pricingVersions"], queryFn: () => api.get("/admin/pricing/versions").then((r) => r.data) });
  return (
    <Section title="Version history" sub="Bookings keep the version they were quoted with.">
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {data?.map((v) => (
          <div key={v.version} className="flex justify-between gap-2 text-xs" data-testid="pricing-version-row">
            <span><span className={`font-semibold ${v.active ? "neon-text-lime" : ""}`}>v{v.version}{v.active ? " · active" : ""}</span> {v.note && <span className="text-muted-foreground">— {v.note}</span>}</span>
            <span className="shrink-0 text-muted-foreground">{v.created_by} · {when(v.created_at)}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ScheduleSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["scheduleSettings"], queryFn: () => api.get("/schedule/settings").then((r) => r.data) });
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (data) setForm(data);
  }, [data]);
  if (!form) return null;
  const save = async () => {
    try {
      await api.put("/admin/schedule/settings", form);
      toast.success("Booking schedule rules saved");
      qc.invalidateQueries({ queryKey: ["scheduleSettings"] });
      qc.invalidateQueries({ queryKey: ["pricingConfig"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Section title="Booking schedule" sub="Applies to every booked or confirmed ride. Riders can't book inside the gap after a ride or sooner than the minimum notice. Admins can override with a warning.">
      <div className="grid grid-cols-2 gap-3" data-testid="schedule-settings">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Gap after each ride</Label>
          <select value={form.gap_minutes} onChange={(e) => setForm({ ...form, gap_minutes: Number(e.target.value) })} data-testid="schedule-gap-select"
            className="h-9 w-full rounded-md border bg-background px-2 text-sm">
            {[5, 10, 15, 20, 25, 30].map((m) => <option key={m} value={m}>{m} minutes</option>)}
          </select>
        </div>
        <Num label="Minimum notice (minutes)" step="1" min="10" value={form.lead_minutes} onChange={(x) => setForm({ ...form, lead_minutes: x })} testid="schedule-lead-input" />
      </div>
      <Button type="button" variant="outline" className="h-9" onClick={save} data-testid="schedule-settings-save">Save schedule rules</Button>
    </Section>
  );
}

export default function AdminPricing() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adminPricing"], queryFn: () => api.get("/admin/pricing").then((r) => r.data) });
  const [rules, setRules] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setRules(data.rules);
  }, [data]);
  if (!rules) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { booking_fee, ...body } = rules; // eslint-disable-line no-unused-vars
      const { data: saved } = await api.put("/admin/pricing", { ...body, note: note || null });
      toast.success(`Pricing v${saved.version} is live. Existing bookings keep their fares.`);
      setNote("");
      qc.invalidateQueries({ queryKey: ["adminPricing"] });
      qc.invalidateQueries({ queryKey: ["pricingVersions"] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up" data-testid="admin-pricing">
      <PageHead title="Pricing" sub={`Scoopz GPS + anchor pricing engine · active version v${data.version}`}>
        <Input placeholder="Change note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-9 w-56" data-testid="pricing-note-input" />
        <Button onClick={save} disabled={busy} data-testid="pricing-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish new version"}</Button>
      </PageHead>
      <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
        <div className="space-y-5">
          <AnchorEditor anchor={rules.anchor} onChange={(anchor) => setRules({ ...rules, anchor })} />
          <VehiclesEditor vehicles={rules.vehicles} onChange={(vehicles) => setRules({ ...rules, vehicles })} />
          <TiersEditor rules={rules} onChange={setRules} />
          <ExtrasEditor rules={rules} onChange={setRules} />
          <FuelReview rules={rules} onChange={setRules} status={data.fuel_review} />
        </div>
        <div className="space-y-5 xl:sticky xl:top-6 xl:self-start">
          <ScheduleSettings />
          <PricingSimulator rules={rules} />
          <Versions />
        </div>
      </div>
    </div>
  );
}
